import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popup, useMap } from "react-map-gl/mapbox";

import { usePolygonDrawContext } from "./PolygonDrawContext";
import { DRAW_MODES } from "./helpers";

import styles from "./DrawCursorPopup.module.css";

// How close (in screen pixels) the cursor must be to the polygon's first point
// before we offer the "close shape" hint.
const CLOSE_HINT_THRESHOLD_PX = 7;

/**
 * Drawing hints for the polygon currently being drawn:
 *  - no points placed yet            -> "Add first point" (follows the cursor)
 *  - 3+ points and cursor near start -> "Close shape" (pinned to the start point)
 *
 * mapbox-gl-draw clears the selection while in draw_polygon mode, so we can't
 * read the in-progress polygon from the draw API. Instead we track the points
 * placed during the current drawing session by counting map clicks.
 */
export default function DrawCursorPopup() {
  const { t } = useTranslation();
  const { current: map } = useMap();
  const { isActive, mode } = usePolygonDrawContext();
  const [hint, setHint] = useState(null);
  const pointsRef = useRef([]);

  const drawing = isActive && mode === DRAW_MODES.DRAW;

  useEffect(() => {
    if (!map || !drawing) {
      setHint(null);
      return undefined;
    }

    // Each time we enter draw mode, start a fresh point list.
    pointsRef.current = [];

    const handleClick = (e) => {
      pointsRef.current = [...pointsRef.current, [e.lngLat.lng, e.lngLat.lat]];
    };

    // Polygon finished — clear so the next session starts clean.
    const handleCreate = () => {
      pointsRef.current = [];
      setHint(null);
    };

    const handleMove = (e) => {
      const points = pointsRef.current;
      const committed = points.length;

      // Before the first click: prompt to add the first point.
      if (committed === 0) {
        setHint({
          lng: e.lngLat.lng,
          lat: e.lngLat.lat,
          anchor: "left",
          messageKey: "risksMap.polygon.addFirstPoint",
        });
        return;
      }

      // Once the polygon can close (>=3 points), only hint when the cursor is
      // hovering very close to this polygon's first point.
      if (committed >= 3) {
        const first = points[0];
        const firstPx = map.project(first);
        const dist = Math.hypot(firstPx.x - e.point.x, firstPx.y - e.point.y);
        if (dist <= CLOSE_HINT_THRESHOLD_PX) {
          setHint({
            lng: first[0],
            lat: first[1],
            anchor: "bottom",
            offset: 12,
            messageKey: "risksMap.polygon.closeShape",
          });
          return;
        }
      }

      setHint(null);
    };

    const clear = () => setHint(null);

    map.on("click", handleClick);
    map.on("draw.create", handleCreate);
    map.on("mousemove", handleMove);
    map.on("mouseout", clear);
    return () => {
      map.off("click", handleClick);
      map.off("draw.create", handleCreate);
      map.off("mousemove", handleMove);
      map.off("mouseout", clear);
    };
  }, [map, drawing]);

  if (!drawing || !hint) return null;

  return (
    <Popup
      longitude={hint.lng}
      latitude={hint.lat}
      closeButton={false}
      closeOnClick={false}
      anchor={hint.anchor}
      offset={hint.offset ?? 16}
      maxWidth="300px"
      className={styles.popup}
    >
      <span style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}>
        {t(hint.messageKey)}
      </span>
    </Popup>
  );
}
